// Note: This directive requires Highcharts libraries to work.

// Given an object like this in the $scope:
// $scope.notSaving = {
//   percentage : 25.0,
//   number : 12,
//   numberIsPercentage : false, // Defaults to false if left off.
//   label : 'label',
//   color : '#f15a28'
// };
//
// And a directive like this in the HTML:
// <div aptitude-dial-gauge gauge-value="notSaving"></div>
//
// You will get a dial gauge 25% filled with a reddish orange color. There will
// be a large number 12 in the center with the text "label" underneath it. If
// any of the values of the $scope.notSaving object are changed then the chart
// will immediately update to reflect the changes.
//
// If numberIsPercentage is true, then the number displayed in the middle of the
// dial will have '%' appended.
export default function AptitudeDialGauge() {
  'ngInject';

  return {
    restrict: 'A',
    scope: {
      notApplicable: '=',
      gaugeValue: '='
    },
    template: '<div style="width: 150px; height: 150px; margin: 0 auto"></div>',
    replace: true,
    controller: _aptitudeDialGaugeController
  };
}

export function _aptitudeDialGaugeController($scope, $element) {
  'ngInject';

  let data = $scope.gaugeValue;

  // Expose these three helper functions on the $scope for testing purposes.
  $scope._generateTitle = generateTitle;
  $scope._generateData = generateData;
  $scope._initializeGraph = initializeGraph;

  function generateTitle(
    numberVal,
    numberIsPercentage,
    notApplicable,
    label,
    color
  ) {
    let percentageString = '';
    if (numberIsPercentage) {
      percentageString = '<span style="font-size: 18px;">%</span>';
    }

    if (label == null) {
      label = '';
    }

    if (notApplicable) {
      numberVal = 'N/A';
    }

    let numberAndLabel =
      '<span style="font-weight: bold;">' +
      numberVal +
      percentageString +
      '</span><br><span style="font-size: 11px; color: #6d6e70;">' +
      label +
      '</span>';

    return {
      text: numberAndLabel,
      style: {
        'font-size': '25px',
        color: color
      },
      align: 'center',
      verticalAlign: 'middle'
    };
  }
  let lightgray = '#d3d3d3';

  function generateData(label, color, notColor, percentage, notApplicable) {
    let percentageColor = color;

    if (notApplicable) {
      percentageColor = lightgray;
    }

    // Make an array of either one or two numbers. If the incoming value is either zero or 100 then we don't need two
    // numbers.
    let retVal = [
      {
        name: label,
        color: percentageColor,
        y: percentage
      },
      {
        name: 'not',
        color: notColor,
        y: 100 - percentage
      }
    ];

    return retVal;
  }

  function initializeGraph($element, data, notApplicable) {
    // We're using the title on the chart to display both the central
    // number and the label (if any).
    let title = generateTitle(
      data.number,
      data.numberIsPercentage,
      notApplicable,
      data.label,
      data.color
    );

    data.notColor = data.notColor || lightgray;
    let seriesData = generateData(
      data.label,
      data.color,
      data.notColor,
      data.percentage,
      notApplicable
    );

    // Consult the documentation here to understand these many options:
    // http://api.highcharts.com/highcharts
    $element.highcharts({
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: 0,
        plotShadow: false
      },
      title: title,
      tooltip: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      plotOptions: {
        pie: {
          dataLabels: {
            enabled: false
          },
          borderWidth: 0,
          startAngle: -180,
          endAngle: 180,
          center: ['50%', '50%']
        }
      },
      series: [
        {
          type: 'pie',
          name: '',
          innerSize: '90%',
          ignoreHiddenPoint: true,
          data: seriesData
        }
      ]
    });
  }

  // The directive should not freak out if there's no data to display yet.
  if (data != null) {
    initializeGraph($element, $scope.gaugeValue, $scope.notApplicable);
    $scope.alreadyInitialized = true;
  }

  // This watches for any changes in the bound data and immediately updates the chart in response to the change.
  $scope.$watch(
    'gaugeValue',
    function (newValue, oldValue) {
      if (newValue !== oldValue) {
        if ($scope.alreadyInitialized) {
          // If the percentage or color changed we need to update the Highchart series.
          if (
            newValue.percentage != oldValue.percentage ||
            newValue.color != oldValue.color
          ) {
            newValue.notColor = newValue.notColor || lightgray;

            let series = $element.highcharts().series[0];
            let seriesData = generateData(
              newValue.label,
              newValue.color,
              newValue.notColor,
              newValue.percentage,
              $scope.notApplicable
            );

            series.setData(seriesData);
          }

          // For anything else we update the title.
          if (
            newValue.number != oldValue.number ||
            newValue.numberIsPercentage != oldValue.numberIsPercentage ||
            newValue.label != oldValue.label ||
            newValue.color != oldValue.color
          ) {
            let title = generateTitle(
              newValue.number,
              newValue.numberIsPercentage,
              $scope.notApplicable,
              newValue.label,
              newValue.color
            );
            $element.highcharts().setTitle(title);
          }
        } else {
          initializeGraph($element, $scope.gaugeValue, $scope.notApplicable);
          $scope.alreadyInitialized = true;
        }
      }
    },
    true
  );
}
